/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useReducer, Reducer } from 'react'
import styled from 'styled-components'
import { Select } from './Select'
import { SiteDirectory } from '../models/client/site'
import { SearchInput } from './SearchInput'
import { DateRangePicker } from './DateRangePicker'
import { GateFeedFilter, DateFilter } from '../clients/type'
import { device } from '../styles/mixins/device'
import { Check } from './Check'
import { InputContainer } from './InputContainer'
import { ConnectedLoadTypeSelect } from './ConnectedLoadTypeSelect'
import {
  COLUMN_FILTER_PROPERTY_OPTIONS,
  COLUMN_FILTER_SELECT_TYPE_IDS,
} from '../models/client/observation'
import { ConnectedVehicleTypeSelect } from './ConnectedVehicleTypeSelector'
import { SPACING } from '../styles/mixins/constants'
import { ConnectedCarrierSelect } from './ConnectedCarrierSelect'

const PROPERTY_VALUES: string[] = COLUMN_FILTER_PROPERTY_OPTIONS.map(({ value }) => value)

const isPropertyOptionValue = (
  value: string
): value is (typeof COLUMN_FILTER_PROPERTY_OPTIONS)[number]['value'] =>
  PROPERTY_VALUES.includes(value)

type PropertyType = { value: string | null | undefined; label?: string | null }

type State = {
  propertyValue?: PropertyType | null
  propertyError: string | null
  propertyCategory: (typeof COLUMN_FILTER_PROPERTY_OPTIONS)[number]['value'] | null
}

const initialState: State = {
  propertyCategory: null,
  propertyValue: null,
  propertyError: null,
}

type ActionType<T, P> = { type: T; payload: P }

type Actions =
  | ActionType<'PropertyValue', PropertyType>
  | ActionType<'PropertyError', State['propertyError']>
  | ActionType<'PropertyCategory', State['propertyCategory']>
  | ActionType<'ResetProperties', undefined>

const reducer: Reducer<State, Actions> = (state, action) => {
  switch (action.type) {
    case 'PropertyCategory':
      return {
        ...state,
        propertyCategory: action.payload,
        propertyError: null,
      }
    case 'PropertyError':
      return { ...state, propertyError: action.payload }
    case 'PropertyValue':
      return {
        ...state,
        propertyValue: action.payload,
        propertyError: null,
      }
    case 'ResetProperties':
      return {
        ...state,
        propertyValue: null,
        propertyError: null,
      }
    default:
      return { ...state }
  }
}

type FieldName = 'search' | 'bare' | 'date' | 'location' | 'gate' | 'direction'

type Props = {
  className?: string
  sites?: SiteDirectory | null
  filters: GateFeedFilter[]
  fields: FieldName[]
  onAddFilter: (filter: GateFeedFilter) => void
  onRemoveFilter: (filter: GateFeedFilter) => void
}

export const FormGateFeedFilters = (props: Props): JSX.Element => {
  const { className, sites, filters, fields, onAddFilter, onRemoveFilter } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const { propertyCategory, propertyError, propertyValue } = state

  // eslint-disable-next-line consistent-return
  const handleFilterSearch = (): void => {
    if (!propertyCategory) {
      return dispatch({
        type: 'PropertyError',
        payload: 'Please select a filter',
      })
    }
    if (!propertyValue)
      return dispatch({
        type: 'PropertyError',
        payload: 'Please enter a filter value',
      })
    const nextPropertyFilters: GateFeedFilter = {
      type: 'property',
      id: `${propertyCategory}-${propertyValue?.value || 'null'}`,
      label:
        COLUMN_FILTER_PROPERTY_OPTIONS.find((option) => option.value === propertyCategory)?.label ||
        'NEVER',
      displayValue: propertyValue?.label || '',
      value: [
        {
          name: propertyCategory,
          value: propertyValue?.value || '',
        },
      ],
    }

    onAddFilter(nextPropertyFilters)

    return dispatch({ type: 'ResetProperties', payload: undefined })
  }

  let selectedSite = ''
  let selectedGate = ''
  let selectedDirection = ''

  filters.forEach((filter) => {
    if (filter.type === 'direction') {
      selectedDirection = filter.value
    }
  })
  filters.forEach((filter) => {
    if (filter.type === 'site') {
      selectedSite = filter.value.value
    }
  })
  filters.forEach((filter) => {
    if (filter.type === 'gate') {
      selectedGate = filter.value.value
    }
  })
  const options = Object.values(sites || {}).map((site) => ({
    value: site.id,
    label: site.name,
  }))

  let gateOptions = []
  if (selectedSite) {
    gateOptions = Object.values(sites[selectedSite]?.gates || {}).map((gate) => ({
      value: gate.id,
      label: gate.name,
    }))
  } else {
    Object.values(sites || {}).map((site) => {
      Object.values(site.gates || {}).map((gate) => {
        gateOptions.push({ value: gate.id, label: `${site.name} - ${gate.name}` })
      })
    })
  }

  const dateFilters: DateFilter[] = []
  filters.forEach((filter) => {
    if (filter.type === 'date') dateFilters.push(filter)
  })

  const [dateFilter] = dateFilters

  return (
    <Container className={className}>
      <Content>
        {fields.map((fieldName) => {
          switch (fieldName) {
            case 'bare':
              return (
                <Field key={fieldName}>
                  <InputContainer
                    key={fieldName}
                    hasDanger={false}
                    label="Is Bare"
                    disabled={false}
                  >
                    <Check
                      label="Yes"
                      testingKeys={['GateFeedFilterIsBareCheck']}
                      isChecked={Boolean(filters.find((filter) => filter.type === 'isBare')?.value)}
                      onChange={(nextValue) => {
                        if (!nextValue) {
                          onRemoveFilter({
                            type: 'isBare',
                            id: 'isBare',
                            label: 'Is Bare',
                            value: true,
                          })
                          return
                        }
                        onAddFilter({
                          type: 'isBare',
                          id: 'isBare',
                          label: 'Is Bare',
                          value: true,
                        })
                      }}
                    />
                  </InputContainer>
                </Field>
              )
            case 'date':
              return (
                <Field key={fieldName}>
                  <StyledDateRangePicker
                    key={fieldName}
                    label="Date"
                    disableDateRange="future"
                    onChange={(dates) => {
                      onAddFilter({
                        type: 'date',
                        id: `${dates.start || ''}-${dates.end || ''}`,
                        label: 'Date',
                        value: {
                          start: dates.start || undefined,
                          end: dates.end || undefined,
                        },
                      })
                    }}
                    value={dateFilter ? { ...dateFilter.value } : undefined}
                  />
                </Field>
              )
            case 'location':
              return (
                <Field key={fieldName}>
                  <Select
                    key={fieldName}
                    label="Location"
                    noOptionsMessage="No matching location"
                    placeholder="Select a site..."
                    options={options}
                    value={selectedSite}
                    testingKeys={['GateFeedFilterLocationSelect']}
                    onChange={(nextOption) => {
                      const selectedSiteFilter = filters.find((filter) => filter.type === 'site')
                      const siteFilter = options.find((item) => item.value === nextOption)
                      if (!siteFilter && selectedSiteFilter?.type === 'site') {
                        onRemoveFilter(selectedSiteFilter)
                        return
                      }
                      if (!siteFilter) return
                      const { label, value } = siteFilter
                      onAddFilter({
                        type: 'site',
                        label: 'Site ID',
                        id: value,
                        value: {
                          label,
                          value,
                        },
                      })
                    }}
                  />
                </Field>
              )
            case 'gate':
              return (
                <Field key={fieldName}>
                  <Select
                    key={fieldName}
                    label="Gate"
                    noOptionsMessage="No matching gate"
                    placeholder="Select a gate..."
                    options={gateOptions}
                    value={selectedGate}
                    testingKeys={['GateFeedFilterGateSelect']}
                    onChange={(nextOption) => {
                      const selectedGateFilter = filters.find((filter) => filter.type === 'gate')
                      const gateFilter = gateOptions.find((item) => item.value === nextOption)
                      if (!gateFilter && selectedGateFilter?.type === 'gate') {
                        onRemoveFilter(selectedGateFilter)
                        return
                      }
                      if (!gateFilter) return
                      const { label, value } = gateFilter
                      // gateOptions = []
                      onAddFilter({
                        type: 'gate',
                        label: 'Gate ID',
                        id: value,
                        value: {
                          label,
                          value,
                        },
                      })
                    }}
                  />
                </Field>
              )
            case 'direction':
              return (
                <Field key={fieldName}>
                  <Select
                    key={fieldName}
                    label="Direction"
                    noOptionsMessage="No matching location"
                    placeholder="Select a direction..."
                    options={[
                      { label: 'both', value: '' },
                      { label: 'inbound', value: 'true' },
                      { label: 'outbound', value: 'false' },
                    ]}
                    value={selectedDirection}
                    onChange={(nextOption) => {
                      const selectedDirectionFilter = filters.find(
                        (filter) => filter.type === 'direction'
                      )
                      const directionFilter = [
                        { label: 'inbound', value: 'true' },
                        { label: 'outbound', value: 'false' },
                      ].find((item) => item.value === nextOption)
                      if (!directionFilter && selectedDirectionFilter?.type === 'direction') {
                        onRemoveFilter(selectedDirectionFilter)
                        return
                      }
                      if (!directionFilter) return
                      const { label, value } = directionFilter
                      onAddFilter({
                        type: 'direction',
                        label: 'Direction',
                        id: label,
                        value,
                      })
                    }}
                  />
                </Field>
              )
            case 'search':
              return (
                <Field key={fieldName}>
                  <StyledSearchInput
                    label="Search"
                    placeholder="Enter search term..."
                    selectPlaceholder="Category"
                    selectValue={propertyCategory}
                    searchDraftValue={propertyValue?.value || undefined}
                    onSearch={handleFilterSearch}
                    onEnterPress={handleFilterSearch}
                    hasDanger={Boolean(propertyError)}
                    onSelect={(selectedOption) => {
                      if (!isPropertyOptionValue(selectedOption)) return
                      dispatch({
                        type: 'PropertyCategory',
                        payload: selectedOption,
                      })
                    }}
                    onSearchValue={(nextValue) =>
                      dispatch({
                        type: 'PropertyValue',
                        payload: { value: nextValue },
                      })
                    }
                    selectOptions={[...COLUMN_FILTER_PROPERTY_OPTIONS]}
                    testingKeys={['GateFeedFilterPropertySelect']}
                  />
                </Field>
              )
            default:
              return (
                <Field key={fieldName}>
                  <div />
                </Field>
              )
          }
        })}
      </Content>
    </Container>
  )
}

const Container = styled.div``

const Content = styled.div`
  min-width: 0px;
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  flex: 1 1 auto;
  ${device.desktop`
    flex-flow: row;
  `}
`

const Field = styled.div`
  padding-bottom: ${SPACING[1]};
  min-width: 0px;
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  ${device.desktop`
        padding-bottom: 0px;
        padding-right: ${SPACING[1]};
        width: auto;
  `}
  &:last-child {
    padding: 0px;
    flex: 0 0 auto;
    width: auto;
  }
`

const StyledSearchInput = styled(SearchInput)`
  ${SearchInput.Select} {
    min-width: 212px;
  }
`

const StyledDateRangePicker = styled(DateRangePicker)`
  width: 100%;
`
